import React, { useEffect, useState } from "react";
import Button from "../../components/Button";
import AdminSidebar from "../../components/AdminSidebar";
import {
  Upload,
  Database,
  X,
  FileText,
  Brain,
  BookOpen,
  RefreshCw,
  Menu,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  fetchModels,
  trainModelIndex,
  createPromptTemplates,
} from "../../../services/modelApi";
import { SearchableDropdown } from "../../components/SearchableDropdown";
import { Drawer } from "../../components/ui/Drawer";

function ModelTraining() {
  const [modelHandle, setModelHandle] = useState(undefined);
  const [transcriptFiles, setTranscriptFiles] = useState([]); // Changed to array
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const [createLoading, setCreateLoading] = useState(false);
  const [indexes, setIndexes] = useState([]);
  const [allIndexes, setAllIndexes] = useState([]);

  // Dual RAG training state
  const [uploadType, setUploadType] = useState("documents"); // 'documents' | 'prompts'
  const [promptTemplateFiles, setPromptTemplateFiles] = useState([]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Validate file types
    const validFiles = selectedFiles.filter((file) => {
      const validTypes = [".txt", ".pdf", ".json"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      return validTypes.includes(fileExtension);
    });

    if (validFiles.length !== selectedFiles.length) {
      toast.error(
        "Some files were rejected. Only .txt, .pdf, and .json files are allowed."
      );
    }

    // Add new files to existing ones (avoiding duplicates)
    setTranscriptFiles((prevFiles) => {
      const existingFileNames = prevFiles.map((f) => f.name);
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.includes(file.name)
      );

      if (newFiles.length === 0 && validFiles.length > 0) {
        toast.info("Some files were already selected.");
      }

      return [...prevFiles, ...newFiles];
    });

    // Clear the input to allow selecting the same file again if needed
    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setTranscriptFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const removeAllFiles = () => {
    setTranscriptFiles([]);
  };

  // Prompt template file handlers
  const handlePromptFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter((file) => {
      const isValidType =
        file.name.endsWith(".txt") ||
        file.name.endsWith(".json") ||
        file.name.endsWith(".pdf");
      const isValidSize = file.size < 10 * 1024 * 1024; // 10MB limit

      if (!isValidType) {
        toast.error(`File "${file.name}" must be a .txt, .json, or .pdf file`);
        return false;
      }

      if (!isValidSize) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return false;
      }

      return true;
    });

    setPromptTemplateFiles((prevFiles) => {
      const existingNames = prevFiles.map((f) => f.name);
      const newFiles = validFiles.filter(
        (file) => !existingNames.includes(file.name)
      );

      if (newFiles.length === 0 && validFiles.length > 0) {
        toast.info("Some files were already selected.");
      }

      return [...prevFiles, ...newFiles];
    });

    e.target.value = "";
  };

  const removePromptFile = (indexToRemove) => {
    setPromptTemplateFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const removeAllPromptFiles = () => {
    setPromptTemplateFiles([]);
  };

  useEffect(() => {
    fetchHandles();
  }, []);

  const handleTrainModel = async () => {
    try {
      setCreateLoading(true);

      // Validation based on upload type
      if (uploadType === "documents" && transcriptFiles.length === 0) {
        toast.error("Please select at least one document file");
        return;
      }

      if (uploadType === "prompts" && promptTemplateFiles.length === 0) {
        toast.error("Please select at least one prompt template file");
        return;
      }

      if (!modelHandle) {
        toast.error("Please select a model");
        return;
      }

      const formData = new FormData();

      // Append appropriate files based on upload type
      if (uploadType === "documents") {
        transcriptFiles.forEach((file) => {
          formData.append("transcriptFiles", file);
        });
      } else {
        promptTemplateFiles.forEach((file) => {
          formData.append("promptTemplateFiles", file);
        });
        // For prompt templates, we need the customModelId instead of modelHandle
        formData.append("customModelId", modelHandle.value);
      }

      if (uploadType === "documents") {
        formData.append("modelHandle", modelHandle.value);
      }
      formData.append("uploadType", uploadType);

      // Use different API endpoints based on upload type
      let res;
      if (uploadType === "documents") {
        res = await trainModelIndex(formData);
      } else {
        // We'll need to add this function to modelApi.js
        res = await createPromptTemplates(formData);
      }

      const fileCount =
        uploadType === "documents"
          ? transcriptFiles.length
          : promptTemplateFiles.length;
      const fileType =
        uploadType === "documents" ? "documents" : "prompt templates";

      toast.success(`${fileCount} ${fileType} uploaded successfully!`);

      // Reset form
      setModelHandle(undefined);
      setTranscriptFiles([]);
      setPromptTemplateFiles([]);
      setUploadType("documents");
    } catch (error) {
      toast.error(error.message || `Failed to upload ${uploadType}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const fetchHandles = async () => {
    try {
      const res = await fetchModels();

      // Separate knowledge bases and prompt templates based on naming convention
      const allIndexes = res.data.map((item, i) => ({
        label: item.name,
        value: item.name,
        id: i + 1,
        isPromptTemplate: item.name.endsWith("-prompts"),
      }));

      setAllIndexes(allIndexes);

      // Filter based on current upload type
      filterIndexesByUploadType(allIndexes, uploadType);
    } catch (error) {
      toast.error(error);
    }
  };

  const filterIndexesByUploadType = (allIndexes, type) => {
    let filtered;
    if (type === "documents") {
      // For documents, show only knowledge bases (not ending with -prompts)
      filtered = allIndexes.filter((item) => !item.isPromptTemplate);
    } else {
      // For prompts, show only prompt template knowledge bases (ending with -prompts)
      filtered = allIndexes
        .filter((item) => item.isPromptTemplate)
        .map((item) => ({
          ...item,
          label: item.label.replace("-prompts", ""), // Remove -prompts suffix for display
          displayName: item.label.replace("-prompts", ""),
        }));
    }
    setIndexes(filtered);

    // Clear selection if current selection is not valid for new upload type
    if (
      modelHandle &&
      !filtered.find((item) => item.value === modelHandle.value)
    ) {
      setModelHandle(undefined);
    }
  };

  const handleSearch = (query) => {
    setSearchLoading(true);
    // Simulate API call
    setTimeout(() => {
      // Get filtered options based on current upload type
      let baseOptions;
      if (uploadType === "documents") {
        baseOptions = allIndexes.filter((item) => !item.isPromptTemplate);
      } else {
        baseOptions = allIndexes
          .filter((item) => item.isPromptTemplate)
          .map((item) => ({
            ...item,
            label: item.label.replace("-prompts", ""),
            displayName: item.label.replace("-prompts", ""),
          }));
      }

      if (query) {
        const filtered = baseOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        setIndexes(filtered);
      } else {
        setIndexes(baseOptions);
      }
      setSearchLoading(false);
    }, 500);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const [showSidebar, setShowSidebar] = useState(false);
  const onClose = () => {
    setShowSidebar(false);
  };
  return (
    <div className="w-screen min-h-screen bg-gray-800 overflow-x-hidden">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 h-full">
          <AdminSidebar />
        </div>
        <Drawer
          height="full"
          title={"Admin"}
          isOpen={showSidebar}
          onClose={onClose}
        >
          <div className=" lg:hidden  w-full h-full">
            <AdminSidebar />
          </div>
        </Drawer>
        {/* Main Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto bg-gray-800">
          {/* Header */}
          <div className="px-8 py-6 border-b border-gray-700  flex justify-between items-start">
            <div className="flex items-center gap-3">
              <Brain className="hidden lg:block text-green-400" size={24} />
              <div>
                <h1 className=" text-xl lg:text-2xl font-semibold text-white">
                  Update Knowledge Base
                </h1>
                <p className="text-gray-400 text-sm">
                  Add new information to existing AI models to improve their
                  responses
                </p>
              </div>
            </div>
            <div
              onClick={() => {
                setShowSidebar(true);
              }}
              className="max-w-max lg:hidden"
            >
              <Menu size={30} color="white" />
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1 p-3 lg:p-8">
            <div className="max-w-2xl mx-auto bg-gray-900 border border-gray-700 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-8">
                <BookOpen className="text-green-400" size={24} />
                <h2 className="text-lg font-semibold text-white">
                  Knowledge Base Enhancement
                </h2>
              </div>

              {/* Information Section */}
              <div className="mb-8 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-blue-300 mb-2">
                      How This Works
                    </h3>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      This tool allows you to add new information to existing AI
                      models. When you upload transcript files, the system will
                      process them and add the knowledge to the selected model's
                      database. This helps the AI provide more accurate and
                      up-to-date responses based on the new information you
                      provide.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Select AI Bot
                </label>
                <p className="text-xs text-gray-400 mb-3">
                  Choose which AI bot you want to enhance with new knowledge
                </p>
                <SearchableDropdown
                  className="w-full"
                  valueClassName="border border-gray-600 px-3 py-2 w-full bg-gray-800 text-white"
                  value={modelHandle}
                  onChange={(value) => {
                    setModelHandle(value);
                  }}
                  onSearch={handleSearch}
                  options={indexes}
                  slugBased={true}
                  placeholder="Select an AI bot to update..."
                />
              </div>

              {/* Upload Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Upload Type
                </label>
                <div className="flex flex-col lg:flex-row gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setUploadType("documents");
                      filterIndexesByUploadType(allIndexes, "documents");
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      uploadType === "documents"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                    disabled={createLoading}
                  >
                    Knowledge Documents
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadType("prompts");
                      filterIndexesByUploadType(allIndexes, "prompts");
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      uploadType === "prompts"
                        ? "bg-purple-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                    disabled={createLoading}
                  >
                    Prompt Templates
                  </button>
                </div>
                <p className="text-xs text-gray-400">
                  {uploadType === "documents"
                    ? "Upload additional knowledge documents to expand the AI's knowledge base"
                    : "Upload prompt templates to improve response optimization and context handling"}
                </p>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-300">
                    {uploadType === "documents"
                      ? "Upload New Knowledge Files"
                      : "Upload Prompt Templates"}
                  </label>
                  {((uploadType === "documents" &&
                    transcriptFiles.length > 0) ||
                    (uploadType === "prompts" &&
                      promptTemplateFiles.length > 0)) && (
                    <button
                      type="button"
                      onClick={
                        uploadType === "documents"
                          ? removeAllFiles
                          : removeAllPromptFiles
                      }
                      className="text-xs text-red-400 hover:text-red-300 font-medium"
                      disabled={loading || createLoading}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                <p className="text-xs text-gray-400 mb-3">
                  {uploadType === "documents"
                    ? "Upload transcript files (.txt, .pdf) containing new information you want the AI to learn from"
                    : "Upload prompt template files (.txt, .json, .pdf) to improve AI response optimization and context handling"}
                </p>

                {/* Conditional File Upload */}
                {uploadType === "documents" ? (
                  <>
                    {/* Document File Upload */}
                    <div className="flex items-center gap-3 mb-4">
                      <label className="flex w-full justify-center items-center gap-2 cursor-pointer px-4 py-3 bg-gray-800 border border-blue-600 rounded-lg hover:bg-gray-700 transition">
                        <Upload className="text-blue-400" size={20} />
                        <span className="text-sm font-medium text-gray-300">
                          {transcriptFiles.length === 0
                            ? "Choose Knowledge Files"
                            : "Add More Files"}
                        </span>
                        <input
                          type="file"
                          accept=".txt,.pdf,.json"
                          className="hidden"
                          onChange={handleFileChange}
                          disabled={loading || createLoading}
                          multiple
                        />
                      </label>
                      {transcriptFiles.length > 0 && (
                        <span className="text-sm text-blue-400 font-medium">
                          {transcriptFiles.length} file
                          {transcriptFiles.length !== 1 ? "s" : ""} selected
                        </span>
                      )}
                    </div>

                    {/* Selected Document Files */}
                    {transcriptFiles.length > 0 && (
                      <div className="bg-gray-800 border border-blue-600 rounded-lg p-4 max-h-48 overflow-y-auto">
                        <h4 className="text-sm font-semibold text-gray-300 mb-3">
                          Files to be processed:
                        </h4>
                        <div className="space-y-2">
                          {transcriptFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-700 p-3 rounded border border-gray-600"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <FileText
                                  className="text-blue-400 flex-shrink-0"
                                  size={16}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-white truncate">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-red-500/20 transition-colors flex-shrink-0"
                                disabled={loading || createLoading}
                                title="Remove file"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Prompt Template File Upload */}
                    <div className="flex items-center gap-3 mb-4">
                      <label className="flex w-full justify-center items-center gap-2 cursor-pointer px-4 py-3 bg-gray-800 border border-purple-600 rounded-lg hover:bg-gray-700 transition">
                        <Upload className="text-purple-400" size={20} />
                        <span className="text-sm font-medium text-gray-300">
                          {promptTemplateFiles.length === 0
                            ? "Choose Prompt Templates"
                            : "Add More Templates"}
                        </span>
                        <input
                          type="file"
                          accept=".txt,.json,.pdf"
                          className="hidden"
                          onChange={handlePromptFileChange}
                          disabled={loading || createLoading}
                          multiple
                        />
                      </label>
                      {promptTemplateFiles.length > 0 && (
                        <span className="text-sm text-purple-400 font-medium">
                          {promptTemplateFiles.length} template
                          {promptTemplateFiles.length !== 1 ? "s" : ""} selected
                        </span>
                      )}
                    </div>

                    {/* Selected Prompt Template Files */}
                    {promptTemplateFiles.length > 0 && (
                      <div className="bg-gray-800 border border-purple-600 rounded-lg p-4 max-h-48 overflow-y-auto">
                        <h4 className="text-sm font-semibold text-gray-300 mb-3">
                          Templates to be processed:
                        </h4>
                        <div className="space-y-2">
                          {promptTemplateFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-700 p-3 rounded border border-gray-600"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <FileText
                                  className="text-purple-400 flex-shrink-0"
                                  size={16}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-white truncate">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removePromptFile(index)}
                                className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-red-500/20 transition-colors flex-shrink-0"
                                disabled={loading || createLoading}
                                title="Remove template"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Prompt Template Info */}
                    <div className="mt-4 p-3 bg-purple-900/20 border border-purple-600/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-purple-300 mb-1">
                            Prompt Template Guidelines
                          </h3>
                          <ul className="text-xs text-gray-300 space-y-1">
                            <li>
                              • Templates are automatically categorized as
                              system, user, or context prompts
                            </li>
                            <li>
                              • Use template variables with {"{"}
                              {"{"}
                              {"}variable{"}
                              {"}"}
                              {""} syntax for dynamic content
                            </li>
                            <li>
                              • System templates improve the AI's core behavior
                              patterns
                            </li>
                            <li>
                              • Context templates enhance information processing
                              capabilities
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Processing Information */}
              <div className="mb-6 p-4 bg-gray-800 border border-gray-600 rounded-lg">
                <div className="flex items-start gap-3">
                  <RefreshCw
                    className={`flex-shrink-0 mt-1 ${
                      uploadType === "documents"
                        ? "text-blue-400"
                        : "text-purple-400"
                    }`}
                    size={16}
                  />
                  <div>
                    <h3
                      className={`text-sm font-medium mb-2 ${
                        uploadType === "documents"
                          ? "text-blue-300"
                          : "text-purple-300"
                      }`}
                    >
                      What happens next?
                    </h3>
                    <ul className="text-xs text-gray-300 space-y-1">
                      {uploadType === "documents" ? (
                        <>
                          <li>
                            • Files will be processed and converted into
                            searchable knowledge
                          </li>
                          <li>
                            • New information will be added to the AI model's
                            database
                          </li>
                          <li>
                            • The AI will be able to reference this new
                            information in future conversations
                          </li>
                          <li>
                            • Processing may take a few minutes depending on
                            file size
                          </li>
                        </>
                      ) : (
                        <>
                          <li>
                            • Prompt templates will be analyzed and categorized
                            automatically
                          </li>
                          <li>
                            • Templates will be vectorized and stored for
                            contextual retrieval
                          </li>
                          <li>
                            • The AI will use relevant templates to optimize
                            response generation
                          </li>
                          <li>
                            • Prompt optimization will be enabled for this model
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              <Button
                name={
                  uploadType === "documents"
                    ? "Update Knowledge Base"
                    : "Upload Prompt Templates"
                }
                onClick={handleTrainModel}
                isLoading={createLoading}
                type="secondary"
                className={`w-full font-semibold py-3 rounded-lg transition-colors ${
                  uploadType === "documents"
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-purple-600 hover:bg-purple-700"
                } text-white`}
                disabled={
                  createLoading ||
                  !modelHandle ||
                  (uploadType === "documents"
                    ? transcriptFiles.length === 0
                    : promptTemplateFiles.length === 0)
                }
                icon={<Brain size={20} />}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModelTraining;
